import { OntologyFieldDataType } from '@packages/enums';
import type { Evidence } from '@packages/types';
import constate from 'constate';
import { useCallback, useState } from 'react';
import type { EvidenceField, SelectOption } from './types';

export function useEvidenceState() {
    const [open, setOpen] = useState(false);
    const [displayLabel, setDisplayLabel] = useState<string | null>(null);
    const [fieldPath, setFieldPath] = useState<string | null>(null);
    const [dataType, setDataType] = useState<OntologyFieldDataType | null>(null);
    const [selectOptions, setSelectOptions] = useState<SelectOption[] | null>(null);
    const [evidenceData, setEvidenceData] = useState<EvidenceField | null>(null);
    const [selectedSourceFile, setSelectedSourceFile] = useState<string | null>(null);
    const [selectedEvidence, setSelectedEvidence] = useState<Evidence | null>(null);
    const [editEnabled, setEditEnabled] = useState(false);

    const openEvidenceModal = useCallback(
        (
            displayLabel: string,
            evidenceData: EvidenceField,
            selectedSourceFile: string,
            fieldPath: string,
            dataType?: OntologyFieldDataType,
            selectOptions?: SelectOption[],
            editEnabled?: boolean
        ) => {
            setOpen(true);
            setDisplayLabel(displayLabel);
            setFieldPath(fieldPath);
            setDataType(dataType ?? null);
            setSelectOptions(selectOptions ?? null);
            setEvidenceData(evidenceData);
            setSelectedSourceFile(selectedSourceFile);

            setEditEnabled(editEnabled ?? false);
        },
        []
    );

    const closeEvidenceModal = useCallback(() => {
        setOpen(false);
        setDisplayLabel(null);
        setFieldPath(null);
        setDataType(null);
        setSelectOptions(null);
        setEvidenceData(null);
        setSelectedSourceFile(null);
        setSelectedEvidence(null);
        setEditEnabled(false);
    }, []);

    const updateSelectedEvidence = useCallback((evidence: Evidence) => {
        setSelectedEvidence(evidence);
        setSelectedSourceFile(evidence.source_file);
    }, []);

    return {
        openEvidenceModal,
        closeEvidenceModal,
        setSelectedSourceFile,
        updateSelectedEvidence,
        open,
        displayLabel,
        fieldPath,
        dataType,
        selectOptions,
        evidenceData,
        selectedSourceFile,
        selectedEvidence,
        editEnabled,
    };
}

export const [
    EvidenceStateProvider,
    useOpenEvidenceModal,
    useCloseEvidenceModal,
    useEvidenceModalData,
    useUpdateSelectedSourceFile,
    useUpdateSelectedEvidence,
    useEditEnabled,
] = constate(
    useEvidenceState,
    value => value.openEvidenceModal,
    value => value.closeEvidenceModal,
    value => {
        return {
            open: value.open,
            displayLabel: value.displayLabel,
            fieldPath: value.fieldPath,
            dataType: value.dataType,
            selectOptions: value.selectOptions,
            evidenceData: value.evidenceData,
            fileName: value.selectedSourceFile,
            selectedEvidence: value.selectedEvidence,
            editEnabled: value.editEnabled,
        };
    },
    value => value.setSelectedSourceFile,
    value => value.updateSelectedEvidence,
    value => value.editEnabled
);
